import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@rebass/emotion';

import { screenSmall } from '@app/styles/theme/breakpoints';
import useMediaQuery from '@app/helpers/useMediaQuery';
import FormattedMoney from '@app/shop/components/FormattedMoney';

import { useOrderHistoryDetail } from './OrderHistoryDetailContext';
import TotalsRows from './TotalsRows';
import { DetailTable } from './OrderHistoryDetail.styles';
import {
  ItemDescription,
  ItemDescriptionSimplified,
  ItemSimplified,
  ItemPricesSimplified,
  TotalsTableSimplified,
} from './ItemsDetail.styles';
import messages from './ItemsDetail.messages';

const ItemsDetail: FunctionComponent = () => {
  const isMobile = useMediaQuery(`(max-width: ${screenSmall})`, {
    // For some reason going mobile first completely breaks table layout
    ssrMatch: false,
  });
  const { itemsData, storeConfig } = useOrderHistoryDetail();
  const includingTax = storeConfig?.taxSalesDisplayPrice === 'including';

  return isMobile ? (
    <>
      <Box mb={20} data-testid={'history-order-items-detail'}>
        {itemsData?.items?.map((item) =>
          item ? (
            <ItemSimplified pt={20} pb={20} key={item.id}>
              <Box mb={2}>
                <ItemDescriptionSimplified item={item} />
              </Box>
              <Box mb={4}>{item.status}</Box>
              <ItemPricesSimplified>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemPriceHeader} />
                  </dt>
                  <dd data-testid={`history-${item.productSku}-price-mobile`}>
                    {includingTax ? (
                      item?.productSalePriceIncludingTax && (
                        <FormattedMoney
                          {...item.productSalePriceIncludingTax}
                        />
                      )
                    ) : (
                      <FormattedMoney {...item.productSalePrice} />
                    )}
                  </dd>
                </Box>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemQuantityHeader} />
                  </dt>
                  <dd>{item.quantityOrdered}</dd>
                </Box>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemTotalHeader} />
                  </dt>
                  <dd
                    data-testid={`history-${item.productSku}-row-total-mobile`}
                  >
                    {includingTax ? (
                      item?.rowTotalIncludingTax && (
                        <FormattedMoney {...item.rowTotalIncludingTax} />
                      )
                    ) : (
                      <FormattedMoney {...item.rowTotal} />
                    )}
                  </dd>
                </Box>
              </ItemPricesSimplified>
            </ItemSimplified>
          ) : null
        ) ?? null}
      </Box>
      <TotalsTableSimplified>
        <tbody>
          <TotalsRows />
        </tbody>
      </TotalsTableSimplified>
    </>
  ) : (
    <DetailTable>
      <thead>
        <tr>
          <th scope="col">
            <FormattedMessage {...messages.itemProductHeader} />
          </th>
          <th scope="col">
            <FormattedMessage {...messages.itemPriceHeader} />
          </th>
          <th scope="col">
            <FormattedMessage {...messages.itemQuantityHeader} />
          </th>
          <th scope="col">
            <FormattedMessage {...messages.itemTotalHeader} />
          </th>
        </tr>
      </thead>

      <tbody>
        {itemsData?.items?.map((item) =>
          item ? (
            <tr key={item.productSku}>
              <td>
                <Box mb={3}>
                  <ItemDescription item={item} />
                </Box>
                <Box>{item.status}</Box>
              </td>
              <td data-testid={`history-${item.productSku}-price`}>
                {includingTax ? (
                  item?.productSalePriceIncludingTax && (
                    <FormattedMoney {...item.productSalePriceIncludingTax} />
                  )
                ) : (
                  <FormattedMoney {...item.productSalePrice} />
                )}
              </td>
              <td>{item.quantityOrdered}</td>
              <td data-testid={`history-${item.productSku}-row-total`}>
                {includingTax ? (
                  item?.rowTotalIncludingTax && (
                    <FormattedMoney {...item.rowTotalIncludingTax} />
                  )
                ) : (
                  <FormattedMoney {...item.rowTotal} />
                )}
              </td>
            </tr>
          ) : null
        ) ?? null}
        <TotalsRows />
      </tbody>
    </DetailTable>
  );
};

export default ItemsDetail;
